import { useCallback, useEffect } from "react";

import { Flex, IconButton, Box } from "@chakra-ui/react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";

import { useHandleSharedListNameUpdate } from "app/hooks/useHandleSharedListNameUpdate";
import { routes } from "config/routes";
import { EndDroppingProcessButton } from "flows/Inbound/components/EndDroppingProcessButton";
import { TranslatedProduct } from "flows/Inbound/stores/inboundStore/types";
import { useInboundStore } from "flows/Inbound/stores/inboundStore/useInboundStore";
import { useClaimInboundList } from "flows/Inbound/stores/useInboundDropService";
import { useInboundPreviewService } from "flows/Inbound/stores/useInboundPreviewService";
import { Page } from "shared/components/Page";
import { SpinnerModal } from "shared/components/SpinnerModal";
import { BOTTOM_TAB_BAR_HEIGHT_NUMBER } from "ui/BottomTabBarContainer";
import { CheckIcon, FilledPencilIcon, NewProfileIcon, ThreedotsIcon } from "ui/Icons/Icons";
import { InboundPreDroppingListItemCard } from "ui/InboundPreDroppingListItemCard/InboundPreDroppingListItemCard";
import { Menu } from "ui/Menu/Menu";
import { NavigationHeaderWithInput } from "ui/NavigationHeaderWithInput/NavigationHeaderWithInput";

export function InboundPreviewProductCard({ product }: { product: Readonly<TranslatedProduct> }) {
  const {
    totalInboundQuantity,
    handlingUnitSize,
    unitSizeForDisplay,
    isInbounded,
    ...translatedInboundUnit
  } = product;
  const { productSku } = translatedInboundUnit;

  return (
    <InboundPreDroppingListItemCard
      key={productSku}
      translatedInboundUnit={translatedInboundUnit}
      displayQuantity={unitSizeForDisplay}
      displayAsHandlingUnit={handlingUnitSize > 1}
      stockUpdatePlanTotal={totalInboundQuantity}
      isQuantityMatchingExpectation={false}
      isStockUpdateComplete={isInbounded}
      isEnabledFilter={false}
    />
  );
}

export function InboundPreviewProductsPage() {
  const navigate = useNavigate();
  const { claimInboundList } = useClaimInboundList();
  const { isFetchingProducts } = useInboundPreviewService();
  const { newListName, setNewListName, handleUpdateSharedListName } =
    useHandleSharedListNameUpdate();

  const {
    sharedListId,
    sharedListName,
    deliverySSCC,
    deliveryType,
    creatorId,
    orderId,
    inboundPreviewProducts,
    inboundUIState,
    setInboundUIState,
    resetToInitialState,
  } = useInboundStore(
    useShallow((state) => ({
      sharedListName: state.sharedListName,
      sharedListId: state.sharedListId,
      deliverySSCC: state.deliverySSCC,
      deliveryType: state.deliveryType,
      creatorId: state.creatorId,
      orderId: state.orderId,
      inboundPreviewProducts: state.inboundPreviewProducts,
      inboundUnitsSortedByDate: state.inboundUnitsSortedByDate,
      inboundUIState: state.inboundUIState,
      setInboundUIState: state.setInboundUIState,
      resetToInitialState: state.resetToInitialState,
    })),
  );
  const { isSharedListNameEditActive, isOrderCanceled } = inboundUIState;
  const handleClaimInboundListClick = useCallback(() => {
    if (sharedListId) {
      claimInboundList({
        listId: sharedListId,
        deliverySSCC,
        deliveryType,
        creatorId,
        orderId,
        isOrderCanceled,
      });
    }
  }, [
    claimInboundList,
    creatorId,
    deliverySSCC,
    deliveryType,
    isOrderCanceled,
    orderId,
    sharedListId,
  ]);

  useEffect(() => {
    if (!sharedListName) navigate(routes.inbound.root);
  }, [navigate, sharedListName]);

  return (
    <Page isFull isBgGrey h="100%">
      <NavigationHeaderWithInput
        title={newListName}
        onClickGoBack={() => {
          resetToInitialState();
          navigate(routes.inbound.root);
        }}
        onTitleChange={(value) => {
          setNewListName(value);
        }}
        isDisabled={!isSharedListNameEditActive}
        rightElement={
          isSharedListNameEditActive ? (
            <CheckIcon
              width="24px"
              height="24px"
              cursor="pointer"
              onClick={handleUpdateSharedListName}
            />
          ) : (
            <Menu>
              <Menu.Trigger
                as={IconButton}
                icon={<ThreedotsIcon boxSize="24px" />}
                variant="unstyled"
                aria-label="Options"
                w="24px"
                h="24px"
                textAlign="end"
              />
              <Menu.List
                zIndex={50}
                minWidth="140px"
                borderRadius="sm"
                width="auto"
                mt="-45px"
                boxShadow="lg"
              >
                <Menu.Item
                  icon={<NewProfileIcon boxSize={4} />}
                  sx={{
                    _hover: {
                      bg: "transparent",
                    },
                  }}
                  onClick={handleClaimInboundListClick}
                  paddingY="s150"
                >
                  <FormattedMessage id="flows.inbound.components.shared-list-component.menu-claim" />
                </Menu.Item>
                {!isOrderCanceled && (
                  <Menu.Item
                    icon={<FilledPencilIcon boxSize={4} />}
                    sx={{
                      _hover: {
                        bg: "transparent",
                      },
                    }}
                    paddingY="s150"
                    onClick={() => {
                      setInboundUIState({ isSharedListNameEditActive: true });
                    }}
                  >
                    <FormattedMessage id="flows.inbound.components.shared-list-component.menu-edit" />
                  </Menu.Item>
                )}
              </Menu.List>
            </Menu>
          )
        }
      />
      <Flex
        direction="column"
        pos="relative"
        gap="s150"
        flex={1}
        overflowY="scroll"
        w="100%"
        pt="s200"
        onClick={() => {
          // TO_DO
        }}
      >
        {inboundPreviewProducts?.length > 0 &&
          inboundPreviewProducts.map((product) => (
            <InboundPreviewProductCard key={product.productSku} product={product} />
          ))}
        <Box mb="60px" />
        <EndDroppingProcessButton
          onClickButton={handleClaimInboundListClick}
          labelMessageId="flows.inbound.components.shared-list-component.claim-button"
          bottom={BOTTOM_TAB_BAR_HEIGHT_NUMBER}
        />
      </Flex>
      <SpinnerModal isOpen={isFetchingProducts} />
    </Page>
  );
}
